import { useState, useContext, useMemo, useRef } from 'react';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import { DeployBlockTimer } from '../../Components/DeployBlockTimer';
import TrashIcon from '../../Components/assets/delete.svg';
import Collapsible from '../../Components/Collapsible';
import { getProviderLink } from '../../utils/bubbles/link';
import { getTwitterIdFromUrl } from '../../utils/ge-tweet-id-from-url';
import { HoverTracker } from '../../Components/HoverTracker';
import { useLatestCallbackRef } from '../../Hooks/useLatestCallbackRef';
import { getSourceDetails } from '../../utils/source-constants';
import { Sorter } from '../../utils/bubbles/sort';
import { SortDropdown, SortItem } from '../../Components/SortDropdown';
import { ReadonlyStore, useFrozenStoreState } from '../../utils/store';
import { HeaderSettingsContainer } from '../../Components/HeaderSettings';
import * as Options from '../../Components/Options';
import * as Virtual from '../../Components/VirtualGrid/VirtualGrid';
import { CopyToClipboard } from '../../Components/CopyToClipboard';
import { TwitterUserFollowers } from '../../Components/twitter/TwitterUserFollowers';
import { TwitterAccountCreatedAt } from '../../Components/twitter/TwitterAccountCreatedAt';
import { TwitterPhoto } from '../../Components/twitter/TwitterPhoto';
import { TelegramIcon } from '../../Components/icons/TelegramIcon';
import { SolanaIcon } from '../../Components/icons/SolanaIcon';
import { SearchIcon } from '../../Components/icons/SearchIcon';
import { ArrowRightIcon } from '../../Components/icons/ArrowRightIcon';
import { Popup } from '../../Components/Popup';
import * as Form from '../../Components/form';

import { FeedBubble, FeedToken } from './types';
import { LinkedCtx } from './context/LinkedContext';

import '../../utils/gradients.css';
import { DexIcon } from '../../Components/icons/DexIcon';
import { Spinner } from '../../Components/Spinner';
import { AppMode } from '../../types';
import { BN } from '@coral-xyz/anchor';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';

type Props<T extends FeedToken> = Virtual.ColumnProps &
    VolumeProps & {
        mode: AppMode;
        dexName: string;
        dexIcon: React.ReactNode;
        bubbleContainerClassname?: string;
        store: ReadonlyStore<{ loading: boolean | null; bubbles: FeedBubble<T>[] }>;
        sortItems: (SortItem & { ASC: Sorter<T>; DESC: Sorter<T> })[];

        defaultMinLinkedTokens?: number;

        onCreate: (token: T) => void;
        onBuy: (token: T, solAmount: BN) => void;
        onRemove: (id: number) => void;

        onMinTokensChange: (min: number) => void;

        LiveDataToken: React.ComponentType<{
            token: T;
            children: (token: T, bondingCurve: number | null, marketCap: number | null) => React.ReactNode;
        }>;
        LinkedCtx: LinkedCtx;
    };

export const DexFeedColumn = <T extends FeedToken>({
    id,
    width,
    mode,
    dexName,
    dexIcon,
    bubbleContainerClassname = '',
    store,
    sortItems,
    defaultMuted,
    defaultMinLinkedTokens = 3,
    onCreate,
    onBuy,
    onRemove,
    onMinTokensChange,
    onVolumeToggle,
    LiveDataToken,
    LinkedCtx,
}: Props<T>) => {
    const [displayLinked, setDisplayLinked] = useState<boolean>(true);
    const [displayUnlinked, setDisplayUnlinked] = useState<boolean>(true);

    const onCreateRef = useLatestCallbackRef(onCreate);
    const onBuyRef = useLatestCallbackRef(onBuy);
    const onRemoveRef = useLatestCallbackRef(onRemove);
    const onVolumeToggleRef = useLatestCallbackRef(onVolumeToggle);

    const [minLinkedTokens, setMinLinkedTokens] = useState(defaultMinLinkedTokens);

    const [isColumnInFocus, setIsColumnInFocus] = useState(false);

    const state = useFrozenStoreState(store, () => isColumnInFocus);

    const activeBubbles = useMemo(
        () =>
            state.bubbles
                .filter((bubble) => {
                    const isLinked = bubble.twitterUsername !== null;
                    if (isLinked && displayLinked) return true;
                    if (!isLinked && displayUnlinked) return true;
                    return false;
                })
                .filter((bubble) => bubble.tokens.length >= minLinkedTokens),
        [state.bubbles, displayLinked, displayUnlinked, minLinkedTokens],
    );

    const [bubblesOpenState, setBubblesOpenState] = useState<Record<string | number, boolean>>({});

    const [tokenSorter, setTokenSorter] = useState<Sorter<T> | null>(null);

    const [buyValueSol, setBuyValueSol] = useState<number>(0.05);
    return (
        <Virtual.Column
            id={id}
            width={width}
            onColumnBodyEnter={() => {
                setIsColumnInFocus(true);
            }}
            onColumnBodyLeave={() => {
                setIsColumnInFocus(false);
            }}
        >
            <Virtual.Title>
                <Virtual.ColumnHeader key={id} className='relative'>
                    <div className={`h-full flex items-center justify-between text-[12px] font-normal p-[4px] ${isColumnInFocus ? 'blur-sm' : ''}`}>
                        {dexIcon}
                        <span className='flex-1 ml-[10px]'>{dexName}</span>
                        {mode === 'buy' && <SolInput buyValueSol={buyValueSol} setBuyValueSol={setBuyValueSol} />}
                        <HeaderSettingsContainer className='h-[28px]'>
                            <button className='size-[24px] bg-[image:var(--collapse-vertical-icon-svg)] bg-cover cursor-pointer' onClick={() => {}} />
                            <Volume defaultMuted={defaultMuted} onVolumeToggle={onVolumeToggleRef.current} />
                            <Options.Options>
                                <Options.MenuRow>
                                    <Options.MenuColumn title='DISPLAY'>
                                        <Options.MenuLabel>
                                            <Form.Checkbox
                                                checked={displayLinked}
                                                onChange={() => {
                                                    setDisplayLinked((state) => !state);
                                                }}
                                            />
                                            Linked
                                        </Options.MenuLabel>
                                        <Options.MenuLabel>
                                            <Form.Checkbox
                                                checked={displayUnlinked}
                                                onChange={() => {
                                                    setDisplayUnlinked((state) => !state);
                                                }}
                                            />
                                            Unlinked
                                        </Options.MenuLabel>
                                    </Options.MenuColumn>
                                    <Options.MenuColumn title='SOUND'>
                                        <Options.MenuLabel>
                                            <Form.Checkbox checked={true} onChange={() => {}} />
                                            Linked
                                        </Options.MenuLabel>
                                        <Options.MenuLabel>
                                            <Form.Checkbox checked={true} onChange={() => {}} />
                                            Unlinked
                                        </Options.MenuLabel>
                                    </Options.MenuColumn>
                                </Options.MenuRow>
                                <Options.MenuRow>
                                    <SortDropdown
                                        label='Sort'
                                        items={sortItems}
                                        onSort={(key, order) => {
                                            setTokenSorter(() => sortItems.find((item) => item.key === key)?.[order] ?? null);
                                        }}
                                    />
                                </Options.MenuRow>
                                <Options.MenuRow>
                                    <div className='w-full flex justify-between items-center'>
                                        <span>Minimum Linked Tokens</span>
                                        <Form.Input
                                            className='w-[40px] shrink-0'
                                            pattern='[0-9]*'
                                            value={minLinkedTokens}
                                            onChange={(e) => {
                                                const min = parseInt(e.target.value || '0');
                                                onMinTokensChange(min);
                                                setMinLinkedTokens(min);
                                            }}
                                        />
                                    </div>
                                </Options.MenuRow>
                            </Options.Options>
                        </HeaderSettingsContainer>
                    </div>
                    {isColumnInFocus && <span className='absolute inset-0 m-auto h-[28px] w-[61px] flex items-center info'>Feed Paused</span>}
                </Virtual.ColumnHeader>
            </Virtual.Title>
            {state.loading ? (
                <Virtual.Block key={`${id} loading`} id={`${id} loading`}>
                    <h3 className='flex justify-center'>Loading...</h3>
                </Virtual.Block>
            ) : (
                activeBubbles.map((bubble) => (
                    <Virtual.Block key={bubble.id} id={bubble.id}>
                        <Bubble
                            key={bubble.id}
                            mode={mode}
                            containerClassname={bubbleContainerClassname}
                            bubble={bubble}
                            open={bubblesOpenState[bubble.id] ?? true}
                            tokenSorter={tokenSorter}
                            onToggle={() => {
                                setBubblesOpenState({
                                    ...bubblesOpenState,
                                    [bubble.id]: !(bubblesOpenState[bubble.id] ?? true),
                                });
                            }}
                            onRemove={onRemoveRef.current}
                            onBuy={onBuyRef.current}
                            onNavigate={onCreateRef.current}
                            LiveDataToken={LiveDataToken}
                            LinkedCtx={LinkedCtx}
                            buyValueSol={buyValueSol}
                            setBuyValueSol={setBuyValueSol}
                        />
                    </Virtual.Block>
                ))
            )}
        </Virtual.Column>
    );
};

function Bubble<T extends FeedToken>({
    mode,
    containerClassname = '',
    bubble,
    open,
    tokenSorter,

    onToggle,
    onBuy,
    onRemove,
    onNavigate,

    LiveDataToken,
    LinkedCtx,

    buyValueSol,
    setBuyValueSol,
}: {
    mode: AppMode;
    containerClassname?: string;
    bubble: FeedBubble<T>;
    open: boolean;
    tokenSorter: Sorter<T> | null;

    onToggle: () => void;
    onBuy: (token: T, value: BN) => void;
    onRemove: (id: number) => void;
    onNavigate: (token: T) => void;

    LiveDataToken: React.ComponentType<{
        token: T;
        children: (token: T, bondingCurve: number | null, marketCap: number | null) => React.ReactNode;
    }>;
    LinkedCtx: LinkedCtx;

    buyValueSol: number;
    setBuyValueSol: (value: number) => void;
}) {
    const markup = useMemo(() => {
        return (
            <>
                <div className='h-[43px] flex items-center px-[9px]'>
                    <span className='mr-[10px]'>{bubble.count}</span>

                    <SourceInfo bubble={bubble} LinkedCtx={LinkedCtx} />

                    {bubble.twitterUsername && (
                        <div className='w-[78px] flex flex-col items-center mr-[16px]'>
                            <TwitterUserFollowers
                                className='text-medium'
                                username={bubble.twitterUsername}
                                format={TwitterUserFollowers.compactFormatter.format}
                            />
                            <TwitterAccountCreatedAt
                                className='text-medium'
                                username={bubble.twitterUsername}
                                format={TwitterAccountCreatedAt.compactFormatter.format}
                            />
                        </div>
                    )}

                    <button
                        className='size-[24px] cursor-pointer mr-[10px] ml-auto'
                        onClick={() => {
                            onRemove(bubble.id);
                        }}
                    >
                        <img src={TrashIcon} alt='Remove Bubble' />
                    </button>

                    <button
                        className={`size-[24px] cursor-pointer bg-[image:var(--collapse-icon-svg)] ${
                            open ? 'rotate-180 transition-transform duration-300' : 'transition-all duration-100 ease-in-out'
                        }`}
                        onClick={() => {
                            onToggle();
                        }}
                    />
                </div>
                <Collapsible open={open}>
                    {(tokenSorter ? bubble.tokens.toSorted(tokenSorter) : bubble.tokens).map((token) => {
                        return (
                            <DeployBlockTimer key={token.mint} deployBlockTime={token.deployBlockTime} timers={[15 * 60 * 1000]}>
                                {(formattedElapsedTime, [bondingCurveTimerCommpleted]) => (
                                    <LiveDataToken token={token}>
                                        {(_, bondingCurve, marketCap) => {
                                            return (
                                                <div
                                                    key={token.mint}
                                                    className='relative h-[37px] flex items-center justify-between bg-white/8 mb-[3px] p-[3px]'
                                                >
                                                    <div
                                                        className='h-[37px] absolute left-0 bg-light-grey opacity-24 pointer-events-none'
                                                        style={{
                                                            width: `${Math.min(bondingCurve ?? 0, 100)}%`,
                                                        }}
                                                    />
                                                    <img className='size-[32px] rounded-[6px]' src={token.image ?? undefined} alt={`${token.name} logo`} />

                                                    <Popup
                                                        content={
                                                            <TokenDetails
                                                                mode={mode}
                                                                token={token}
                                                                bondingCurveProgress={bondingCurve ?? 0}
                                                                buyValueSol={buyValueSol}
                                                                setBuyValueSol={setBuyValueSol}
                                                                onBuy={() => onBuy(token, new BN(buyValueSol * LAMPORTS_PER_SOL))}
                                                            />
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                'w-[100px] flex flex-col text-[10px] ' + ((bondingCurve ?? 0) >= 100 ? 'text-lime-green' : '')
                                                            }
                                                        >
                                                            <span className='peer truncate'>{token.name}</span>
                                                            <span className='peer truncate font-bold'>{token.symbol}</span>
                                                        </div>
                                                    </Popup>
                                                    <div className='w-[32px] flex justify-center'>
                                                        {!bondingCurveTimerCommpleted && (
                                                            <span className='text-[16px] font-bold'>
                                                                {(bondingCurve ?? 0) >= 100 && marketCap ? (
                                                                    <span className='text-lime-green'>{formatMarketCapDisplay(marketCap)}</span>
                                                                ) : (
                                                                    <span>{(bondingCurve ?? 0).toFixed(0) + '%'}</span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <Popup anchor='body' content={<DexScreenerEmbed mint={token.mint} />}>
                                                        <DexIcon className='size-[20px] bg-cover' />
                                                    </Popup>

                                                    <CopyToClipboard className='size-[20px]' copy={token.mint} />

                                                    <HoverTracker as='div'>
                                                        {(hovered) => (
                                                            <>
                                                                <a href={token.twitter ?? undefined} target='_blank' rel='noopener noreferrer'>
                                                                    <div className='size-[17px] bg-[image:var(--x-logo-svg)] bg-cover cursor-pointer opacity-49' />
                                                                </a>
                                                                <div className='absolute z-999'>
                                                                    {hovered && token.twitter && (
                                                                        <TwitterTweetEmbed
                                                                            tweetId={getTwitterIdFromUrl(token.twitter)}
                                                                            options={{ width: 365, theme: 'dark' }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                    </HoverTracker>

                                                    <div className='flex flex-col text-[10px]'>
                                                        <span className='font-bold'>{formattedElapsedTime}</span>
                                                        <span className='font-light'>{new Date(token.deployBlockTime * 1000).toLocaleTimeString()}</span>
                                                    </div>
                                                    <button
                                                        className='size-[24px]'
                                                        onClick={() => {
                                                            if (mode === 'deploy') onNavigate(token);
                                                            else if (mode === 'buy') onBuy(token, new BN(buyValueSol * LAMPORTS_PER_SOL));
                                                        }}
                                                    >
                                                        <ArrowRightIcon className='w-full h-full cursor-pointer' />
                                                    </button>
                                                </div>
                                            );
                                        }}
                                    </LiveDataToken>
                                )}
                            </DeployBlockTimer>
                        );
                    })}
                </Collapsible>
            </>
        );
    }, [bubble, LinkedCtx, open, tokenSorter, onRemove, onToggle, LiveDataToken, mode, buyValueSol, setBuyValueSol, onBuy, onNavigate]);

    return (
        <Container classname={containerClassname} bubble={bubble} LinkedCtx={LinkedCtx}>
            {markup}
        </Container>
    );
}

function Container<T extends FeedToken>({
    classname = '',
    bubble,
    LinkedCtx,
    children,
}: React.PropsWithChildren<{ classname?: string; bubble: FeedBubble<T>; LinkedCtx: LinkedCtx }>) {
    const linkedMap = useContext(LinkedCtx);
    const linkedPost = linkedMap[bubble.id];

    const linked = { color: '', stroke: '', image: '' };

    if (linkedPost) {
        const details = getSourceDetails(linkedPost);

        linked.color = details.color;
        linked.stroke = details.stroke;
        linked.image = details.image;
    }

    return (
        <div className={`flex flex-col text-[12px] font-normal ${linkedPost ? linked.color + ' border-[3px] ' + linked.stroke : classname}`}>{children}</div>
    );
}

function SourceInfo<T extends FeedToken>({ bubble, LinkedCtx }: { bubble: FeedBubble<T>; LinkedCtx: LinkedCtx }) {
    const linkedMap = useContext(LinkedCtx);
    const linkedPost = linkedMap[bubble.id];

    const linked = { color: '', stroke: '', image: '' };

    if (linkedPost) {
        const details = getSourceDetails(linkedPost);

        linked.color = details.color;
        linked.stroke = details.stroke;
        linked.image = details.image;
    }

    return (
        <>
            {linkedPost ? (
                <div className={'flex flex-grow flex-shrink-0 max-w-fit items-center p-[4px] rounded-[4px] bg-midnight border-[2px] ' + linked.stroke}>
                    <img
                        className={'flex w-6 h-6 mr-[10px] border-[2px] rounded-[50%] ' + linked.stroke}
                        src={`data:image/jpeg;base64,${linked.image}`}
                        alt='avatar'
                    />
                    <span className='text-[12px] font-normal mr-[14px]'>{linkedPost.username}</span>
                </div>
            ) : (
                <>
                    <a
                        className='size-[32px] mr-[10px]'
                        href={bubble.twitterUsername ? `https://x.com/${bubble.twitterUsername}` : undefined}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {bubble.twitterUsername ? (
                            <TwitterPhoto
                                className={'size-[32px] rounded-full border-[2px] ' + (linked.stroke || 'border-[var(--color-gray-dust)]')}
                                username={bubble.twitterUsername}
                                fallback={<SearchIcon />}
                            />
                        ) : (
                            <SearchIcon />
                        )}
                    </a>
                    <span className='flex-1'>{bubble.twitterUsername ?? 'Unlinked'}</span>
                </>
            )}
        </>
    );
}

function formatMarketCapDisplay(value: number): string {
    return Intl.NumberFormat('en', {
        notation: 'compact',
        maximumFractionDigits: 0,
    }).format(value);
}

type VolumeProps = {
    defaultMuted: boolean;
    onVolumeToggle: (muted: boolean) => void;
};

function Volume({ defaultMuted, onVolumeToggle }: VolumeProps) {
    const [muted, toggle] = useState(defaultMuted);

    return (
        <button
            className={'size-[24px] bg-cover cursor-pointer ' + (muted ? 'bg-[image:var(--mute-icon-svg)]' : 'bg-[image:var(--volume-icon-svg)]')}
            onClick={() => {
                toggle(!muted);
                onVolumeToggle(!muted);
            }}
        />
    );
}

function SolInput({ buyValueSol, setBuyValueSol }: { buyValueSol: number; setBuyValueSol: (value: number) => void }) {
    return (
        <Form.Input
            className='w-[81px] h-[25px] p-[4px] pr-[8px]'
            inputClassName='text-[12px] text-right font-normal'
            icon={<SolanaIcon className='size-[20px]' />}
            value={buyValueSol}
            onChange={(e) => setBuyValueSol(parseFloat(e.target.value))}
        />
    );
}

function TokenDetails({
    mode,
    token,
    bondingCurveProgress,
    buyValueSol,
    setBuyValueSol,
    onBuy,
}: {
    mode: AppMode;
    token: FeedToken;
    bondingCurveProgress: number;
    buyValueSol: number;
    setBuyValueSol: (value: number) => void;
    onBuy: (token: FeedToken, value: number) => void;
}) {
    return (
        <div className='flex items-center bg-dark-charcoal border-[3px] border-[var(--color-midnight)] py-[4px] px-[2px] text-light-grey'>
            <img className='size-[100px] shrink-0 rounded-[4px]' src={token.image ?? undefined} alt={`${token.name} logo`} />
            <div className='flex flex-col ml-[4px] gap-[18px] px-[12px]'>
                <span>{token.mint}</span>
                <div className='flex items-center'>
                    <div className='flex flex-col flex-1 leading-none'>
                        <span className='text-[14px]'>{token.name}</span>
                        <span className='text-[14px] font-bold'>{token.symbol}</span>
                    </div>
                    {mode === 'buy' && (
                        <>
                            <SolInput buyValueSol={buyValueSol} setBuyValueSol={setBuyValueSol} />
                            <button
                                className='h-[25px] bg-indigo-bright rounded-[4px] text-[10px] font-bold px-[12px] ml-[12px]'
                                onClick={() => onBuy(token, buyValueSol)}
                            >
                                Buy
                            </button>
                        </>
                    )}
                </div>
                <div className='flex items-center'>
                    <span className='w-[100px] text-[16px] font-bold text-right'>{bondingCurveProgress.toFixed(0) + '%'}</span>
                    <div className='flex justify-end w-full gap-[5px]'>
                        <a href={getProviderLink('bonk', token.mint)} target='_blank' rel='noopener noreferrer'>
                            <div className='size-[20px] bg-[image:var(--bonk-logo-svg)] bg-cover cursor-pointer' />
                        </a>
                        <a href={token.telegram ?? undefined} target='_blank' rel='noopener noreferrer'>
                            <TelegramIcon className='size-[20px] opacity-49' />
                        </a>
                        <a href={token.website ?? undefined} target='_blank' rel='noopener noreferrer'>
                            <div className='size-[20px] bg-[image:var(--web-logo-svg)] bg-cover cursor-pointer opacity-49' />
                        </a>
                        <a href={`https://dexscreener.com/solana/${token.mint}`} target='_blank' rel='noopener noreferrer'>
                            <div className='size-[20px] bg-[image:var(--dex-logo-svg)] bg-cover cursor-pointer' />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DexScreenerEmbed({ mint }: { mint: string }) {
    const [isLoaded, setIsLoaded] = useState(false);

    const iframeRef = useRef(
        <iframe
            src={`https://dexscreener.com/solana/${mint}?embed=1&loadChartSettings=0&tabs=0&info=0&chartLeftToolbar=0&chartTheme=dark&theme=dark&chartStyle=1&chartType=usd&interval=1S`}
            title='Dexscreener Chart'
            className='fixed top-1/2 left-1/2 w-[90vw] max-w-[800px] h-[90vh] max-h-[600px] -translate-x-1/2 -translate-y-1/2 border-0'
            onLoad={() => {
                setIsLoaded(true);
            }}
        />,
    );

    return (
        <div key={mint} id='dexscreener-embed'>
            <div className={isLoaded ? '' : 'opacity-0'}>{iframeRef.current}</div>
            {!isLoaded && (
                <div className='fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-dark-charcoal w-[800px] h-[600px] border-[3px] border-[var(--color-midnight)] blur-sm flex justify-center items-center'>
                    <Spinner />
                </div>
            )}
            <style>
                {`
                    @media (min-width: 1400px) {
                        #dexscreener-embed {
                        padding-bottom: 65% !important;
                        }
                    }
                `}
            </style>
        </div>
    );
}
